<template>
  <aside class="picker-summary">
    <div class="card mb-2">
      <div class="card-header bg-light py-2">
        <div class="fw-semibold">
          {{ $t("forwards.picker.summary.all") }}:
          {{
            $t("forwards.common.mwh_value", {
              value: formatNumberWithSpaces(totalCapacity),
            })
          }}
          ·
          {{
            $t("forwards.picker.summary.pools_count", {
              count: rowsInScope.length,
            })
          }}
        </div>
        <div class="fw-semibold" data-testid="summary-taken">
          {{ $t("forwards.picker.summary.taken") }}:
          {{
            $t("forwards.common.mwh_value", {
              value: formatNumberWithSpaces(totalTaken),
            })
          }}
          ·
          {{
            $t("forwards.picker.summary.pools_count", {
              count: takenRows.length,
            })
          }}
        </div>
      </div>
    </div>

    <p v-if="takenRows.length === 0" class="text-body-tertiary fs-10">
      {{ $t("forwards.picker.summary.nothing_taken") }}
    </p>

    <template v-else>
      <p class="text-body-tertiary fs-10">
        {{ $t("forwards.picker.summary.pool_qualifications") }}
      </p>
      <div data-breakdown="vintage" class="card mb-2">
        <div class="card-header bg-light py-1">
          <small>
            <strong>{{ $t("forwards.picker.summary.by_period") }}</strong>
          </small>
        </div>
        <div class="card-body p-0">
          <table class="table table-sm mb-0">
            <tbody>
              <tr v-for="entry in byVintage" :key="entry.label">
                <td class="px-3 py-1">{{ entry.label }}</td>
                <td class="px-3 py-1 text-end">
                  {{ formatNumberWithSpaces(entry.mwh) }}
                </td>
                <td class="px-3 py-1 text-end">
                  {{ ((entry.mwh / totalTaken) * 100).toFixed(1) }}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="card-footer bg-transparent py-1">
          <small class="text-body-tertiary">
            {{ $t("forwards.picker.summary.by_period_estimate") }}
          </small>
        </div>
      </div>

      <div
        v-for="group in groupedBreakdowns"
        :key="group.title"
        :data-breakdown="group.key"
        class="card mb-2"
      >
        <div class="card-header bg-light py-1">
          <small>
            <strong>{{ group.title }}</strong>
            <span
              v-if="group.judgedAtQualificationDate && qualificationDate"
              class="text-body-tertiary ms-1"
              data-testid="summary-judged-at"
            >
              {{
                $t("forwards.picker.summary.judged_at", {
                  date: formatDate(qualificationDate),
                })
              }}
            </span>
          </small>
        </div>
        <div class="card-body p-0">
          <table class="table table-sm mb-0">
            <tbody>
              <tr v-for="entry in group.entries" :key="entry.label">
                <td class="px-3 py-1">{{ entry.label }}</td>
                <td class="px-3 py-1 text-end">
                  {{ formatNumberWithSpaces(entry.mwh) }}
                </td>
                <td class="px-3 py-1 text-end">
                  {{ ((entry.mwh / totalTaken) * 100).toFixed(1) }}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </aside>
</template>

<script setup lang="ts">
import { formatDate, formatNumberWithSpaces } from "@/composables/formatUtils";
import type { PickerPoolRow } from "@/forwards2/forwardsTypes";
import type { TakeMap } from "@/forwards2/picker/distributeTakes";
import { volumeByVintage } from "@/forwards2/picker/productionBreakdown";
import { technologyName } from "@/types/technology";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * What the current selection adds up to, beside the take table — the picker's answer to the spot
 * sale's breakdown column.
 *
 * Country, price and technology are read off the pool, which is exact: a take belongs whole to the
 * pool it came from, and a pool that spans two countries is reported as that pair rather than split
 * between them. The production rows are the one estimate here, and say so: the exact split is
 * decided by the backend when the deal is created. They are by vintage, each pool at its own width
 * (user, 2026-10-20).
 *
 * @property rows - Every pool the query returned; what has been taken is read off these, so a take
 *   stays counted after a header filter hides its row.
 * @property rowsInScope - The rows the take table's header filters leave on screen, which is what
 *   the "All" line describes.
 * @property qualificationDate - The day the rows' COD age, RE100 and OK-power "new" (which a device
 *   ages out of) are judged at, named in those three headers; null while no
 *   answer is loaded
 */
const props = defineProps<{
  rows: PickerPoolRow[];
  rowsInScope: PickerPoolRow[];
  takes: TakeMap;
  qualificationDate: string | null;
}>();

const { t } = useI18n();

interface BreakdownEntry {
  label: string;
  mwh: number;
}

const totalCapacity = computed(() =>
  props.rowsInScope.reduce((sum, row) => sum + row.capacity_total, 0)
);

const takenRows = computed(() =>
  props.rows.filter((row) => (props.takes[row.pool_id] ?? 0) > 0)
);

const totalTaken = computed(() =>
  takenRows.value.reduce((sum, row) => sum + props.takes[row.pool_id], 0)
);

const byVintage = computed<BreakdownEntry[]>(() =>
  volumeByVintage(
    takenRows.value.map((row) => ({
      take: props.takes[row.pool_id],
      capacity_by_period: row.capacity_by_period,
    }))
  )
);

const groupedBreakdowns = computed(() => [
  {
    title: t("forwards.picker.summary.by_country"),
    entries: groupBy((row) =>
      row.countries.length > 0
        ? row.countries.join("/")
        : t("forwards.common.unknown")
    ),
  },
  {
    title: t("forwards.picker.summary.by_price"),
    entries: groupBy(priceConditionLabel),
  },
  {
    title: t("forwards.picker.summary.by_technology"),
    entries: groupBy((row) =>
      row.technologies.length > 0
        ? row.technologies.map(technologyName).join(", ")
        : t("forwards.common.unknown")
    ),
  },
  {
    key: "cod",
    title: t("forwards.picker.summary.by_cod"),
    judgedAtQualificationDate: true,
    entries: groupBy((row) =>
      row.oldest_cod_age == null
        ? t("forwards.common.unknown")
        : t(
            `forwards.picker.summary.${row.oldest_cod_age <= 5 ? "cod_young" : row.oldest_cod_age <= 15 ? "cod_middle" : "cod_old"}`
          )
    ),
  },
  {
    key: "re100",
    title: t("forwards.picker.summary.re100"),
    judgedAtQualificationDate: true,
    entries: groupBy((row) =>
      t(
        row.re100_applicable == null
          ? "common.unknown"
          : row.re100_applicable
            ? "common.yes"
            : "common.no"
      )
    ),
  },
  {
    key: "ecolabels",
    title: t("forwards.picker.summary.by_ecolabel"),
    judgedAtQualificationDate: true,
    entries: ["ekoenergy", "okpower", "okpower_new"].map((key) => ({
      label: t(`forwards.picker.ecolabel.${key}`),
      mwh: takenRows.value
        .filter((row) => row.ecolabels.includes(key))
        .reduce((sum, row) => sum + props.takes[row.pool_id], 0),
    })),
  },
  {
    key: "support",
    title: t("forwards.picker.summary.by_support"),
    entries: groupBy((row) => t(`forwards.common.support.${row.support}`)),
  },
]);

function priceConditionLabel(row: PickerPoolRow): string {
  const floor =
    row.minimum_price === null
      ? t("forwards.picker.take.no_floor")
      : t("forwards.picker.take.floor_value", { value: row.minimum_price });
  return row.contango_condition === "none"
    ? floor
    : `${floor} · ${t(`forwards.picker.take.contango.${row.contango_condition}`)}`;
}

/** Sums the takes of every taken pool sharing one label, biggest first. */
function groupBy(labelOf: (row: PickerPoolRow) => string): BreakdownEntry[] {
  const totals = new Map<string, number>();
  for (const row of takenRows.value) {
    const label = labelOf(row);
    totals.set(label, (totals.get(label) ?? 0) + props.takes[row.pool_id]);
  }
  return [...totals.entries()]
    .map(([label, mwh]) => ({ label, mwh }))
    .sort((left, right) => right.mwh - left.mwh);
}
</script>

<style scoped lang="scss">
.picker-summary {
  width: 22rem;
}
</style>
